import { Icon, Tooltip } from '@lightspeed/design-system-react'
import { PAYMENT_TYPES } from '@vend/business-components'
import { trackUserEvent } from '@vend/utilities'
import React, { useEffect } from 'react'
import { T, useT } from '@transifex/react'
import {
  useIsCalculatingSale,
  useRegisterManager,
} from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleLocalId,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  selectIsTransitioningToPaymentScreenViaLsPay,
  setIsTransitioningToPaymentScreenViaLsPay,
} from '../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { WEB_REGISTER } from '../../web-register.constant'
import { MINIMUM_LSPAY_AMOUNT } from '../sale-model.constant'
import { useLSQuickPay } from './useLSQuickPay'
import { SalePaymentButton } from './sale-payment-button/sale-payment-button'
import { useTransitionToPaymentScreen } from './sale-payment-button/use-transition-to-payment-screen'

export const SalePayments = () => {
  const { showQuickPayButton, disableQuickPayButton } = useLSQuickPay()
  const transitionToPaymentScreen = useTransitionToPaymentScreen(true)
  const saleCalculationRunning = useIsCalculatingSale()
  const registerManager = useRegisterManager()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const saleId = useAppSelector(selectSaleLocalId)
  const isTransitioningToPaymentScreenViaLsPay = useAppSelector(
    selectIsTransitioningToPaymentScreenViaLsPay
  )
  const dispatch = useAppDispatch()
  const t = useT()

  const isAmountBelowMinimum =
    outstandingBalance <= 0 || outstandingBalance < MINIMUM_LSPAY_AMOUNT

  const handleQuickPayClick = () => {
    dispatch(setIsTransitioningToPaymentScreenViaLsPay(true))
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Payment type selected', {
      amount: outstandingBalance,
      paymentTypeId: PAYMENT_TYPES.LSPAY,
      registerSaleId: saleId,
      registerId: registerManager.getActiveRegister().id,
      isQuickPay: true,
      quickPayFeatureFlag: getFeatureFromCache(
        'lspay_pay_with_quick_pay_experiment'
      ),
    })
  }

  useEffect(() => {
    if (
      isTransitioningToPaymentScreenViaLsPay &&
      !saleCalculationRunning &&
      !disableQuickPayButton
    ) {
      dispatch(setIsTransitioningToPaymentScreenViaLsPay(false))
      transitionToPaymentScreen()
    }
  }, [
    disableQuickPayButton,
    dispatch,
    isTransitioningToPaymentScreenViaLsPay,
    saleCalculationRunning,
    transitionToPaymentScreen,
  ])

  return (
    <div className="sale-action-bar" aria-label="payment button groups">
      {showQuickPayButton && (
        <Tooltip
          className="wr-tooltip"
          direction="center"
          message={
            <T
              _str="Payment amount must be at least {minAmount}"
              _context="tooltip message for lightspeed payments minimum amount"
              minAmount={`$${MINIMUM_LSPAY_AMOUNT.toFixed(2)}`}
              lineBreak={<br />}
            />
          }
          header={t('Minimum payment amount required', {
            _context: 'tooltip header for lightspeed payments minimum amount',
          })}
          canBeShown={isAmountBelowMinimum}
          icon="info-circle"
        >
          <button
            type="button"
            className="vd-btn vd-btn--supplementary vd-btn--jumbo vd-ml1"
            data-cy="quick-pay-btn"
            data-testid="quick-pay-btn"
            onClick={handleQuickPayClick}
            disabled={disableQuickPayButton}
          >
            <Icon icon="fa-credit-card"></Icon>
          </button>
        </Tooltip>
      )}
      <SalePaymentButton />
    </div>
  )
}
